import React, { useState } from 'react';

import InlineNameForm from './InlineNameForm';

import Button from 'CommonComponents/Button';
import IconButton from 'CommonComponents/IconButton';

import EditIcon from 'Assets/icons/edit.svg';
import TrashIcon from 'Assets/icons/trash.svg';

/**
 * Asks for a second click before deleting, in place of the row's actions.
 */
function InlineDeleteConfirm({ message, onConfirm, onCancel }) {
    const [deleting, setDeleting] = useState(false);

    const confirm = () => {
        setDeleting(true);
        onConfirm().finally(() => setDeleting(false));
    };

    return (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-p-2 tw-rounded tw-bg-red-25">
            <span className="tw-text-sm tw-text-red tw-me-auto">{message}</span>
            <Button variant="danger" small loading={deleting} disabled={deleting} onClick={confirm}>
                {$t('Delete')}
            </Button>
            <Button variant="outline" small disabled={deleting} onClick={onCancel}>
                {$t('Cancel')}
            </Button>
        </div>
    );
}

function RowActions({ label, canRename, canDelete, onRename, onDelete }) {
    if (!canRename && !canDelete) {
        return null;
    }

    return (
        <div className="tw-flex tw-items-center tw-gap-1">
            {canRename && (
                <IconButton size="sm" ghost title={$t('Rename')} aria-label={`${$t('Rename')} ${label}`} onClick={onRename}>
                    <EditIcon className="tw-w-3 tw-h-3" />
                </IconButton>
            )}
            {canDelete && (
                <IconButton variant="danger" size="sm" ghost title={$t('Delete')} aria-label={`${$t('Delete')} ${label}`} onClick={onDelete}>
                    <TrashIcon className="tw-w-3 tw-h-3" />
                </IconButton>
            )}
        </div>
    );
}

function departmentDeleteMessage(department) {
    const count = department.centres.length;

    if (count === 0) {
        return $t('Delete {{name}}? Every budget line in it will also be deleted.', { name: department.name });
    }

    if (count === 1) {
        return $t('Delete {{name}}? Its centre and every budget line in it will also be deleted.', { name: department.name });
    }

    return $t('Delete {{name}}? Its {{count}} centres and every budget line in them will also be deleted.', { name: department.name, count });
}

function CentreRow({ centre, canRename, canDelete, onRename, onDelete }) {
    const [mode, setMode] = useState(null);

    if (mode === 'rename') {
        return (
            <li className="tw-py-1">
                <InlineNameForm
                    id={`${centre.item_type}_${centre.id}_name`}
                    label={$t('Centre name')}
                    initialValue={centre.name}
                    onSubmit={(name) => onRename(centre, name).then(() => setMode(null))}
                    onCancel={() => setMode(null)}
                />
            </li>
        );
    }

    if (mode === 'delete') {
        return (
            <li className="tw-py-1">
                <InlineDeleteConfirm
                    message={
                        centre.item_type === 'costcenter'
                            ? $t('Delete {{name}}? Its expenses will be deleted and its positions moved to the department.', { name: centre.name })
                            : $t('Delete {{name}}? Its budget lines will also be deleted.', { name: centre.name })
                    }
                    onConfirm={() => onDelete(centre)}
                    onCancel={() => setMode(null)}
                />
            </li>
        );
    }

    return (
        <li className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-min-h-[1.75rem]">
            <span className="tw-text-sm tw-text-charcoal">{centre.name}</span>
            <RowActions label={centre.name} canRename={canRename} canDelete={canDelete} onRename={() => setMode('rename')} onDelete={() => setMode('delete')} />
        </li>
    );
}

/**
 * One department with its centres listed beneath it and an inline "add
 * centre" link. Sport-type departments are read-only here: no rename or
 * delete.
 */
export default function DepartmentCard({
    department,
    typeLabel,
    canEdit,
    canRename,
    canDelete,
    canOpenDetails,
    addCentreLabel,
    onAddCentre,
    onRename,
    onDelete,
}) {
    const [mode, setMode] = useState(null);
    const [addingCentre, setAddingCentre] = useState(false);

    return (
        <div className="tw-px-4 tw-py-3 tw-rounded-lg tw-border tw-border-charcoal-15 tw-bg-background tw-flex tw-flex-col tw-gap-y-2">
            {mode === 'rename' ? (
                <InlineNameForm
                    id={`${department.item_type}_${department.id}_name`}
                    label={$t('Department name')}
                    initialValue={department.name}
                    onSubmit={(name) => onRename(department, name).then(() => setMode(null))}
                    onCancel={() => setMode(null)}
                />
            ) : (
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-4">
                    <h4 className="tw-text-sm tw-font-medium tw-text-charcoal tw-mb-0">{department.name}</h4>
                    <div className="tw-flex tw-items-center tw-gap-3">
                        {canOpenDetails && !department.read_only && (
                            <a href={department.edit_url} className="tw-text-xs tw-font-medium tw-text-blue hover:tw-underline" data-action="navigate">
                                {$t('Edit details')}
                            </a>
                        )}
                        <RowActions
                            label={department.name}
                            canRename={canRename && !department.read_only}
                            canDelete={canDelete && !department.read_only}
                            onRename={() => setMode('rename')}
                            onDelete={() => setMode('delete')}
                        />
                        <span className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">{typeLabel}</span>
                    </div>
                </div>
            )}

            {mode === 'delete' && (
                <InlineDeleteConfirm message={departmentDeleteMessage(department)} onConfirm={() => onDelete(department)} onCancel={() => setMode(null)} />
            )}

            {(department.centres.length > 0 || canEdit) && (
                <ul className="tw-ms-1 tw-ps-4 tw-mb-0 tw-border-s tw-border-charcoal-25 tw-list-none tw-flex tw-flex-col tw-gap-y-1">
                    {department.centres.map((centre) => (
                        <CentreRow key={centre.id} centre={centre} canRename={canRename} canDelete={canDelete} onRename={onRename} onDelete={onDelete} />
                    ))}

                    {canEdit && (
                        <li className="tw-py-0.5">
                            {addingCentre ? (
                                <InlineNameForm
                                    id={`${department.item_type}_${department.id}_new_centre`}
                                    label={$t('Centre name')}
                                    placeholder={$t('Centre name')}
                                    onSubmit={(name) => onAddCentre(department, name).then(() => setAddingCentre(false))}
                                    onCancel={() => setAddingCentre(false)}
                                />
                            ) : (
                                <button
                                    type="button"
                                    className="tw-p-0 tw-border-0 tw-bg-transparent tw-text-xs tw-font-medium tw-text-blue hover:tw-underline"
                                    onClick={() => setAddingCentre(true)}
                                >
                                    {addCentreLabel}
                                </button>
                            )}
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
}
